import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';

import { blockType } from '../../../../types';
import BananatagButton from '../../../../componentLibrary/BananatagButton';
import Modal from '../../../../componentLibrary/Modal';
import * as htmlOutputActions from '../../../../actions/htmlOutput';
import DownloadButton from './DownloadButton';
import { request } from '../../../../common/request';
import { ERR_ENDPOINT_UNAUTHORIZED_GENERIC } from '../../../../constants/Errors';
import configs from '../../../../constants/configs';
import * as Styled from './styles';

const PdfModal = ({ dispatch, draftID, blocks, togglePdfModal, showPersonalizationWarning }) => {
    const [pdfBuffer, setPdfBuffer] = useState(null);
    const [isDownloadButtonEnabled, setIsDownloadButtonEnabled] = useState(false);
    const [hasError, setHasError] = useState(false);

    const getPdf = useCallback(async () => {
        try {
            setHasError(false);
            setIsDownloadButtonEnabled(false);

            const { html, htmlWithEmbeddedCustomFonts } = await request.convertHtml({
                draftID,
                blocks,
                useEmbeddedCustomFonts: true,
            });
            dispatch(htmlOutputActions.updatedHtmlBody({ html }));

            if (htmlWithEmbeddedCustomFonts) {
                const buffer = await request.post(configs.downloadPdf, htmlWithEmbeddedCustomFonts);
                setIsDownloadButtonEnabled(true);
                setPdfBuffer(buffer);
            } else {
                setHasError(true);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setHasError(true);
        }
    }, [blocks]);

    const downloadPdfFile = useCallback(() => {
        const byteArray = new Uint8Array(pdfBuffer);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        saveAs(blob, `Bananatag-${Date.now()}.pdf`);
    }, [pdfBuffer]);

    useEffect(() => {
        getPdf();
    }, [getPdf]);

    return (
        <Modal
            width={280}
            height={showPersonalizationWarning && !hasError ? 360 : 240}
            padding={20}
            borderRadius={8}
            labelledBy="download-pdf-modal-title"
            onEsc={togglePdfModal}
        >
            <Styled.Header id="download-pdf-modal-title">Download PDF</Styled.Header>
            <Styled.Alert hasError={hasError}>
                {hasError ? (
                    <>
                        <Styled.ErrorMessageIcon icon="times-octagon" />
                        <Styled.ErrorMessage>{ERR_ENDPOINT_UNAUTHORIZED_GENERIC}</Styled.ErrorMessage>
                    </>
                ) : (
                    <>
                        <Styled.MessageIcon icon="exclamation-circle" />
                        <Styled.MessageWrapper>
                            <Styled.MessageHeader>Fonts or Styling</Styled.MessageHeader>
                            <Styled.Message>
                                Please note PDFs may display non-standard fonts and formatting
                            </Styled.Message>
                            {showPersonalizationWarning && (
                                <Styled.PersonalizationWrapper>
                                    <Styled.MessageHeader>Personalization Tags</Styled.MessageHeader>
                                    <Styled.Message>
                                        Draft containing tags will remain as tags and not shown its actual value in the
                                        PDF.
                                    </Styled.Message>
                                </Styled.PersonalizationWrapper>
                            )}
                        </Styled.MessageWrapper>
                    </>
                )}
            </Styled.Alert>
            <Styled.ButtonWrapper>
                <BananatagButton
                    type="clear"
                    size="small"
                    onClick={togglePdfModal}
                    text="cancel"
                    styling={{
                        marginRight: 10,
                    }}
                />
                {hasError ? (
                    <BananatagButton
                        type="solid"
                        size="small"
                        onClick={getPdf}
                        icon="refresh"
                        text="retry"
                        styling={{
                            borderRadius: '4px',
                        }}
                    />
                ) : (
                    <DownloadButton enabled={isDownloadButtonEnabled} buttonText="download" onClick={downloadPdfFile} />
                )}
            </Styled.ButtonWrapper>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    draftID: _get(state, 'session.collaboration.id', ''),
    blocks: _get(state, 'canvasBlocks.present', []),
});

PdfModal.defaultProps = {
    showPersonalizationWarning: false,
};

PdfModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    draftID: PropTypes.string.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    togglePdfModal: PropTypes.func.isRequired,
    showPersonalizationWarning: PropTypes.bool,
};

export default connect(mapStateToProps)(PdfModal);
